import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import toast from "react-hot-toast";

import { ticketingProvisionApi } from "../../api";
import { rsvpApi } from "../../api/rsvp";
import { FORM_PAYMENT_LABELS } from "../../types/rsvp";
import type { FormPayment, RsvpSettings } from "../../types/rsvp";

type Props = {
  eventId: string;
  token: string;
  settings: RsvpSettings | undefined;
  walkInUrl: string;
  /** Only the owner can create the Ticketing event. */
  canProvision: boolean;
  onChanged: () => Promise<void> | void;
};

const PAID_OPTIONS: FormPayment[] = ["free", "online", "online_or_gate", "gate"];

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);
}

/** Paid RSVP / walk-in setup: Sell on Zoniq, how each form takes money, and the gate QR. */
export const PaidFormsCard: React.FC<Props> = ({ eventId, token, settings, walkInUrl, canProvision, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const qrRef = useRef<HTMLDivElement | null>(null);

  const rsvpPayment = settings?.rsvp_payment ?? "free";
  const walkInPayment = settings?.walk_in_payment ?? "free";

  const run = async (fn: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const printPoster = () => {
    const svg = qrRef.current?.querySelector("svg")?.outerHTML;
    if (!svg) return;
    const win = window.open("", "_blank", "width=640,height=860");
    if (!win) {
      toast.error("Allow pop-ups to print the poster");
      return;
    }
    win.document.write(
      `<!doctype html><html><head><title>Walk-in QR</title><style>
        body{font-family:system-ui,sans-serif;text-align:center;padding:48px}
        h1{font-size:34px;margin:0 0 8px}p{font-size:20px;color:#444;margin:0 0 28px}
        svg{width:420px;height:420px}
      </style></head><body>
        <h1>${escapeHtml(settings?.event_name ?? "Walk-in")}</h1>
        <p>Scan to register and pay for entry</p>
        ${svg}
      </body></html>`,
    );
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <article className="event-rsvp__share-card">
      <div className="event-rsvp__share-card-head">
        <h3 className="event-rsvp__share-card-title">Paid forms</h3>
      </div>

      <p className="event-rsvp__share-lede" style={{ margin: "0 0 12px" }}>
        Guests pay on the form through Zoniq checkout. A guest who pays is in; there is no approval step.
      </p>

      {canProvision ? (
        <button
          type="button"
          className="eventos-primary-btn"
          disabled={busy}
          onClick={() =>
            void run(() => ticketingProvisionApi.provision(eventId, token), "Linked to Zoniq. Paid forms can be turned on.")
          }
        >
          Sell on Zoniq
        </button>
      ) : null}

      <label className="event-rsvp__disable-field">
        <span>RSVP form</span>
        <select
          className="eventos-input"
          value={rsvpPayment}
          disabled={busy}
          aria-label="How the RSVP form takes money"
          onChange={(e) => void run(() => rsvpApi.updateSettings(eventId, { rsvp_payment: e.target.value as FormPayment }, token))}
        >
          {PAID_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {FORM_PAYMENT_LABELS[o]}
            </option>
          ))}
        </select>
      </label>

      <label className="event-rsvp__disable-field">
        <span>Walk-in form</span>
        <select
          className="eventos-input"
          value={walkInPayment}
          disabled={busy}
          aria-label="How the walk-in form takes money"
          onChange={(e) => void run(() => rsvpApi.updateSettings(eventId, { walk_in_payment: e.target.value as FormPayment }, token))}
        >
          {PAID_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {FORM_PAYMENT_LABELS[o]}
            </option>
          ))}
        </select>
      </label>

      {walkInUrl ? (
        <div style={{ marginTop: 12 }}>
          <div className="eventos-label">Walk-in gate QR</div>
          <div ref={qrRef} style={{ background: "#fff", padding: 10, display: "inline-block", borderRadius: 8, margin: "8px 0" }}>
            <QRCodeSVG value={walkInUrl} size={140} level="M" aria-label="Walk-in form QR code" />
          </div>
          <div className="event-rsvp__share-card-actions">
            <button type="button" className="eventos-nav-btn event-rsvp__share-card-btn" onClick={printPoster}>
              Print poster
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
};
